import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  OverviewAnalytics,
  EngagementTrendsData,
  UserAnalyticsResponse,
  GroupAnalyticsResponse,
  RoleAnalyticsResponse,
  TopPerformer,
  StrugglingUser,
  CompletionBreakdown,
  UserAnalyticsFilters,
  ExportParams
} from '@/types/courseAssignmentAnalytics.types';

class CourseAssignmentAnalyticsService {
  private readonly baseUrl = '/api/assignment-analytics';

  // Get overview analytics with granularity in body
  async getOverviewAnalytics(granularity: 'weekly' | 'monthly' | 'quarterly' | 'yearly' = 'monthly'): Promise<OverviewAnalytics> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: OverviewAnalytics;
    }>(`${this.baseUrl}/overview`, {
      granularity
    });

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch overview analytics');
    }

    return response.data.data;
  }

  // Get engagement trends with configurable granularity
  async getEngagementTrends(
    period: string = '30d',
    granularity: 'hourly' | 'daily' | 'weekly' = 'daily'
  ): Promise<EngagementTrendsData> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: EngagementTrendsData;
    }>(`${this.baseUrl}/engagement-trends`, {
      params: { period, granularity }
    });

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch engagement trends');
    }

    return response.data.data;
  }

  // Get user analytics with filters (POST with body payload)
  async getUserAnalytics(filters: UserAnalyticsFilters = {}): Promise<UserAnalyticsResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: UserAnalyticsResponse;
    }>(`${this.baseUrl}/user-analytics`, filters);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch user analytics');
    }

    return response.data.data;
  }

  // Get all user analytics without pagination (uses export endpoint)
  async getAllUserAnalytics(filters: Omit<UserAnalyticsFilters, 'page' | 'limit'> = {}): Promise<UserAnalyticsResponse> {
    // Build API params - include all filters but NO skip/limit (pagination)
    const apiParams: any = {};

    // Add all filters from the UI
    if (filters.search) {
      apiParams.search = filters.search;
    }

    if (filters.status) {
      apiParams.status = filters.status;
    }

    if (filters.engagement) {
      apiParams.engagement = filters.engagement;
    }

    if (filters.sort) {
      apiParams.sort = filters.sort;
    }

    if (filters.groupKey) {
      apiParams.groupKey = filters.groupKey;
    }

    if (filters.roleKey) {
      apiParams.roleKey = filters.roleKey;
    }

    if (filters.courseId) {
      apiParams.courseId = filters.courseId;
    }

    // Course date filters
    if (filters.created_from) {
      apiParams.created_from = filters.created_from;
    }

    if (filters.created_to) {
      apiParams.created_to = filters.created_to;
    }

    if (filters.completed_from) {
      apiParams.completed_from = filters.completed_from;
    }

    if (filters.completed_to) {
      apiParams.completed_to = filters.completed_to;
    }

    // Call export API endpoint - expecting JSON response with all users
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: UserAnalyticsResponse;
    }>(API_ENDPOINTS.EXPORT.USER_ANALYTICS, apiParams);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch user analytics');
    }

    return response.data.data;
  }

  // Get group analytics with pagination
  async getGroupAnalytics(page: number = 1, limit: number = 20, searchQuery: string = '', engagementLevel: string = '', completionRange: string = '', group_key?: string): Promise<GroupAnalyticsResponse> {
    // Build request body
    const body: any = {
      page,
      limit,
    };

    // Add optional filters to body
    if (searchQuery) {
      body.search = searchQuery;
    }
    if (engagementLevel && engagementLevel !== 'all') {
      body.engagementLevel = engagementLevel;
    }
    if (completionRange && completionRange !== 'all') {
      body.completionRange = completionRange;
    }
    if (group_key) {
      body.group_key = group_key;
    }

    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: GroupAnalyticsResponse;
    }>(`${this.baseUrl}/group-analytics`, body);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch group analytics');
    }

    return response.data.data;
  }

  // Get role analytics with pagination
  async getRoleAnalytics(page: number = 1, limit: number = 20, searchQuery: string = '', engagementLevel: string = '', completionRange: string = '', role_key?: string): Promise<RoleAnalyticsResponse> {
    // Build request body
    const body: any = {
      page,
      limit,
    };

    // Add optional filters to body
    if (searchQuery) {
      body.search = searchQuery;
    }
    if (engagementLevel && engagementLevel !== 'all') {
      body.engagementLevel = engagementLevel;
    }
    if (completionRange && completionRange !== 'all') {
      body.completionRange = completionRange;
    }
    if (role_key) {
      body.role_key = role_key;
    }

    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: RoleAnalyticsResponse;
    }>(`${this.baseUrl}/role-analytics`, body);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch role analytics');
    }

    return response.data.data;
  }

  // Get top performers
  async getTopPerformers(limit: number = 10): Promise<TopPerformer[]> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: TopPerformer[];
    }>(`${this.baseUrl}/top-performers`, {
      params: { limit }
    });

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch top performers');
    }

    return response.data.data;
  }

  // Get struggling users
  async getStrugglingUsers(limit: number = 20): Promise<StrugglingUser[]> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: StrugglingUser[];
    }>(`${this.baseUrl}/struggling-users`, {
      params: { limit }
    });

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch struggling users');
    }

    return response.data.data;
  }

  // Get completion breakdown
  async getCompletionBreakdown(): Promise<CompletionBreakdown> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: CompletionBreakdown;
    }>(`${this.baseUrl}/completion-breakdown`);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch completion breakdown');
    }

    return response.data.data;
  }

  // Export analytics data
  async exportAnalytics(params: ExportParams): Promise<any> {
    const response = await apiClient.get(`${this.baseUrl}/export`, {
      params,
      responseType: params.format !== 'json' ? 'blob' : 'json'
    });

    if (params.format !== 'json') {
      // Handle file download for CSV and Excel
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const extension = params.format === 'csv' ? 'csv' : 'xlsx';
      link.setAttribute('download', `analytics-${params.reportType}-${timestamp}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export downloaded successfully' };
    }

    return response.data;
  }

  // Helper method to format time
  formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  // Helper method to get status color
  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'not_started': return 'text-gray-600 bg-gray-50';
      case 'overdue': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  // Helper method to get engagement color
  getEngagementColor(engagement: string): string {
    switch (engagement) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }

  // Get BU Head dropdown - returns active users who are BU heads
  async getBuHeadDropdown(): Promise<Array<{ _id: string; email: string; employee_id: string; name: string }>> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: Array<{ _id: string; email: string; employee_id: string; name: string }>;
    }>(`${this.baseUrl}/bu-head-dropdown`);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch BU Head dropdown');
    }

    return response.data.data;
  }

  // Get Reporting Manager dropdown - returns active users who are RMs
  async getReportingManagerDropdown(): Promise<Array<{ _id: string; email: string; employee_id: string; name: string }>> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: Array<{ _id: string; email: string; employee_id: string; name: string }>;
    }>(`${this.baseUrl}/rm-dropdown`);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch Reporting Manager dropdown');
    }

    return response.data.data;
  }

  // Get HRBP dropdown - returns active users who are HRBPs
  async getHrbpDropdown(): Promise<Array<{ _id: string; email: string; employee_id: string; name: string }>> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: Array<{ _id: string; email: string; employee_id: string; name: string }>;
    }>(`${this.baseUrl}/hrbp-dropdown`);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch HRBP dropdown');
    }

    return response.data.data;
  }
}

export const courseAssignmentAnalyticsService = new CourseAssignmentAnalyticsService();